/**
 * Admin panel: Free Score Audits.
 *
 * Lists every row from /api/admin/score-previews where role='free-score-audit'
 * (i.e. calls to the new /api/free-score/audit endpoint). Shows the richer
 * fields we now capture: score, percentile, confidence, cohort match, and
 * whether the visitor eventually signed up.
 *
 * Kept intentionally compact: it sits on the admin dashboard next to the
 * legacy "Free score submissions" panel so ops can eyeball funnel quality
 * at a glance.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { RefreshCw, Loader2, Sparkles, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = typeof localStorage !== 'undefined' ? localStorage.getItem('le_token') : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtDate(v) {
  if (!v) return '—';
  try {
    return format(new Date(v), 'MMM d, HH:mm');
  } catch {
    return '—';
  }
}

function confidenceTone(conf) {
  const c = (conf || '').toLowerCase();
  if (c === 'high') return 'bg-emerald-600 hover:bg-emerald-600 text-white';
  if (c === 'medium') return 'bg-sky-600 hover:bg-sky-600 text-white';
  return 'bg-amber-500 hover:bg-amber-500 text-white';
}

function cohortTone(match) {
  const m = (match || '').toLowerCase();
  if (m === 'high') return 'text-emerald-500';
  if (m === 'medium') return 'text-sky-500';
  if (m === 'low' || m === 'global') return 'text-amber-500';
  return 'text-muted-foreground';
}

export function FreeScoreAuditsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/score-previews`, {
        headers: authHeaders(),
        params: { limit: 500 },
        timeout: 20000,
      });
      const all = res?.data?.items || [];
      // Filter to the new /free-score/audit rows only. The legacy widget
      // rows (role != 'free-score-audit') stay in their own panel.
      setRows(all.filter((r) => r.role === 'free-score-audit'));
    } catch (_err) {
      toast.error('Could not load free-score audits.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const signedUpCount = rows.filter((r) => r.converted_to_signup).length;

  return (
    <Card className="border-border mt-6" data-testid="admin-free-score-audits-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
          <Sparkles size={18} className="text-primary" />
          <h2 className="font-semibold">Free score audits</h2>
          <Badge variant="secondary" className="ml-1" data-testid="admin-free-score-audits-count">
            {rows.length}
          </Badge>
          {rows.length > 0 && (
            <Badge
              variant="outline"
              className="text-[10px] border-emerald-400/50 text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10"
              data-testid="admin-free-score-audits-conversion"
            >
              {signedUpCount} signed up · {Math.round((signedUpCount / rows.length) * 100)}%
            </Badge>
          )}
          <span className="text-xs text-muted-foreground hidden md:inline">
            Every LinkedIn URL scored via the new AI-classified free audit endpoint.
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="ml-auto"
            data-testid="admin-free-score-audits-refresh"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </Button>
        </div>

        {rows.length === 0 && !loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No free-score audits yet. Once visitors run the score on /free-score they&apos;ll show up here.
          </div>
        ) : (
          <div className="max-h-[440px] overflow-auto">
            <Table className="min-w-[980px]">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead>Submitted</TableHead>
                  <TableHead>LinkedIn URL</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Percentile</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Cohort match</TableHead>
                  <TableHead>Persona</TableHead>
                  <TableHead>Signed up?</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const persona = row.persona || {};
                  const personaLabel = [
                    persona.careerStage,
                    persona.function && persona.function !== 'any' ? persona.function : null,
                    persona.industry && persona.industry !== 'any' ? persona.industry : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <TableRow key={row.id} data-testid={`admin-free-score-audit-row-${row.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <a
                          href={row.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          <span className="truncate max-w-[240px] inline-block align-middle">
                            {row.linkedin_url}
                          </span>
                          <ExternalLink size={12} className="opacity-60" />
                        </a>
                        {row.cached && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[9px] uppercase tracking-wider text-muted-foreground"
                          >
                            Cached
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        <span className="tabular-nums font-semibold">{row.current_score ?? '—'}</span>
                        {row.potential_score != null && (
                          <>
                            <span className="text-muted-foreground"> → </span>
                            <span className="tabular-nums text-sky-500">{row.potential_score}</span>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">
                        {row.percentile != null ? `${row.percentile}` : '—'}
                      </TableCell>
                      <TableCell>
                        {row.confidence ? (
                          <Badge className={`text-[10px] ${confidenceTone(row.confidence)}`}>
                            {row.confidence}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className={`text-xs uppercase tracking-wider ${cohortTone(row.cohort_match)}`}>
                        {row.cohort_match || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                        <span className="truncate inline-block max-w-full align-middle" title={personaLabel}>
                          {personaLabel || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {row.converted_to_signup ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">Yes</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not yet</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FreeScoreAuditsPanel;
